'use client';

import { memo, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CircleCheck, LoaderCircle, Percent, Square, XCircle } from 'lucide-react';
import { apiFetch } from '@/lib/api/apiClient';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';

interface ProgressOverlayProps {
    isVisible: boolean;
    progress: number; // 0..100（内部値。表示は箱数に合わせて10刻み）
    status:
        | 'preparing'
        | 'uploading'
        | 'success'
        | 'error'
        | 'checking'
        | 'compiling'
        | 'compiled'
        | 'sending'
        | 'writing';
    message: string;
    // コンパイル待ちの概算人数。0人以下やコンパイル中以外では表示しない
    queueAhead?: number | null;
    // 順番待ちキャンセルボタンから/bridge/build/cancel/{job_id}を呼ぶためのjob_id
    jobId?: string | null;
}

/** 合計10本 */
const PROGRESS_SEGMENTS = [
    { color: 'bg-color-schoomy1', count: 3 },
    { color: 'bg-color-schoomy2', count: 4 },
    { color: 'bg-color-schoomy3', count: 1 },
    { color: 'bg-color-schoomy4', count: 2 },
];

const BAR_ANIMATION_INTERVAL = 100;

const STATUS_CONFIG: Record<string, { icon: any; color: string; spin: boolean }> = {
    checking: { icon: LoaderCircle, color: 'text-color-schoomy4', spin: true },
    compiling: { icon: LoaderCircle, color: 'text-color-schoomy4', spin: true },
    compiled: { icon: CircleCheck, color: 'text-color-schoomy4', spin: false },
    sending: { icon: LoaderCircle, color: 'text-color-schoomy4', spin: true },
    preparing: { icon: LoaderCircle, color: 'text-color-schoomy4', spin: true },
    uploading: { icon: LoaderCircle, color: 'text-color-schoomy4', spin: true },
    writing: { icon: LoaderCircle, color: 'text-color-schoomy4', spin: true },
    success: { icon: CircleCheck, color: 'text-color-white-base', spin: false },
    error: { icon: XCircle, color: 'text-red-500', spin: false },
};

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}

// プログレスバー本体(displayedBars)が100msごとに再レンダリングされるため、吹き出しをstatusと
// queueAheadだけに依存するmemo化した別コンポーネントに切り出し、無関係な再レンダリングの
// 影響を受けないようにする。
const CompileBubble = memo(function CompileBubble({
    status,
    queueAhead,
    jobId,
}: {
    status: ProgressOverlayProps['status'];
    queueAhead?: number | null;
    jobId?: string | null;
}) {
    // コンパイル中でなくなったら(次回また新しく表示されるように)キャンセル押下状態をリセットする
    const [cancelling, setCancelling] = useState(false);
    useEffect(() => {
        if (status !== 'compiling') setCancelling(false);
    }, [status]);

    const visible = status === 'compiling' && !!queueAhead && queueAhead > 0;

    const handleCancel = () => {
        if (cancelling || !jobId) return;
        setCancelling(true);
        apiFetch(`/bridge/build/cancel/${jobId}`, { method: 'POST' }).catch(() => {
            // 失敗時は再試行できるようにキャンセル状態を戻す
            setCancelling(false);
        });
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                    className="absolute bottom-full left-0 z-50 mb-4 w-max max-w-[92vw] rounded-3xl bg-white/70 p-3"
                >
                    <div className="flex items-end gap-2">
                        <ImageAdaptive src="/images/ai/compile-01.png" baseWidth={64} className="flex-shrink-0" />
                        <div className="flex w-max items-center gap-3 rounded-2xl rounded-bl-sm border-2 border-amber-400 bg-amber-50 px-3 py-2 font-corp font-medium text-sm leading-relaxed text-color-base shadow-md">
                            <p className="whitespace-nowrap">
                                今、プログラムを機械の言葉に変換中だ。
                                <br />
                                これは時間がかかる作業だから順番に進めるぞ。
                            </p>
                            {!!queueAhead && queueAhead > 0 && (
                                <>
                                    <p className="flex-shrink-0 whitespace-nowrap font-bold">
                                        現在{queueAhead + 1}番目
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        disabled={cancelling}
                                        aria-label={cancelling ? 'キャンセル中' : '順番待ちをキャンセル'}
                                        className={`pointer-events-auto flex-shrink-0 rounded-full p-1.5 shadow-md transition-colors active:scale-90 ${
                                            cancelling
                                                ? 'bg-gray-300 cursor-not-allowed'
                                                : 'bg-red-600 ring-2 ring-red-300 animate-pulse hover:bg-red-700 hover:ring-red-400 cursor-pointer'
                                        }`}
                                    >
                                        <IconAdaptive
                                            Icon={Square}
                                            strokeWidth={0}
                                            fill="currentColor"
                                            baseSize={8}
                                            breakpoints={{ md: 8 }}
                                            className={cancelling ? 'text-color-base' : 'text-white'}
                                        />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
});

export default function ProgressOverlay({ isVisible, progress, status, message, queueAhead, jobId }: ProgressOverlayProps) {
    const bars = useMemo(() => PROGRESS_SEGMENTS.flatMap((seg) => Array(seg.count).fill(seg.color)), []);
    const totalBars = bars.length;

    // 内部progress(0..100) → 0..10本（必ず10刻み相当）
    const targetActiveBars = useMemo(() => {
        const p = clamp(progress, 0, 100);
        if (p >= 100) return totalBars;
        // 0..99 を 0..9 に（successだけ100%で10本）
        return Math.floor(p / 10);
    }, [progress, totalBars]);

    const [displayedBars, setDisplayedBars] = useState(0);

    // バーのアニメーション（増える方向のみゆっくり、減る方向は即）
    useEffect(() => {
        if (targetActiveBars > displayedBars) {
            const timer = setInterval(() => {
                setDisplayedBars((prev) => {
                    if (prev >= targetActiveBars) {
                        clearInterval(timer);
                        return targetActiveBars;
                    }
                    return prev + 1;
                });
            }, BAR_ANIMATION_INTERVAL);

            return () => clearInterval(timer);
        }

        if (targetActiveBars < displayedBars) {
            setDisplayedBars(targetActiveBars);
        }
    }, [targetActiveBars, displayedBars]);

    useEffect(() => {
        if (!isVisible) setDisplayedBars(0);
    }, [isVisible]);

    const config = STATUS_CONFIG[status] || { icon: LoaderCircle, color: 'text-color-schoomy4', spin: true };

    // 表示%は「塗れてる箱数 × 10」に固定（箱と数字を必ず一致させる）
    const displayProgress = status === 'success' ? 100 : displayedBars * 10;

    const hasStarted =
        status === 'uploading' ||
        status === 'success' ||
        status === 'error' ||
        status === 'writing' ||
        status === 'sending' ||
        status === 'compiling' ||
        status === 'compiled' ||
        status === 'checking' ||
        status === 'preparing';

    if (!isVisible) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex flex-grow items-center gap-3 px-4"
        >
            <div className="flex bg-color-white-base rounded-md p-1">
                {bars.map((colorClass, index) => (
                    <div key={index} className="flex w-[46px] px-[3px] h-[13px]">
                        {index < displayedBars && (
                            <motion.div
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{ duration: 0.15, ease: 'easeOut' }}
                                className={`w-full h-full rounded-sm ${colorClass} shadow-right-bottom-mid`}
                                style={{ transformOrigin: 'left' }}
                            />
                        )}
                    </div>
                ))}
            </div>

            <div className="flex items-center gap-2 font-cantarell font-bold text-xs text-color-white-base">
                <IconAdaptive
                    Icon={config.icon}
                    strokeWidth={3}
                    baseSize={14}
                    breakpoints={{ md: 14 }}
                    className={`${config.color} ${config.spin ? 'animate-spin' : ''}`}
                />
                {hasStarted && status !== 'error' && (
                    <>
                        <span>{displayProgress}</span>
                        <IconAdaptive
                            Icon={Percent}
                            strokeWidth={2.0}
                            baseSize={12}
                            breakpoints={{ md: 12 }}
                            className="pl-[2px]"
                        />
                    </>
                )}
            </div>

            <div className="relative flex items-center font-cantarell font-medium text-xs text-color-white-base">
                {message}
                <CompileBubble status={status} queueAhead={queueAhead} jobId={jobId} />
            </div>
        </motion.div>
    );
}
